/**
 * SettingsTab — User configuration for Chanelle OS.
 *
 * Covers:
 *   - Layout presets (apply/preview)
 *   - Theme preference
 *   - Default tab on load
 *   - Watched boards (for Board Pulse)
 *   - Priority weights
 *   - Notification preferences
 *   - Danger zone (reset to factory defaults)
 */

import { useCallback, useState } from 'react';
import {
  Button,
  Dropdown,
  Divider,
  Heading,
  Label,
  Toggle,
} from 'monday-ui-react-core';
import { useSettings } from '../../core/hooks/useSettings';
import { APP_TABS } from '../../core/utils/defaults';
import type { PresetName } from '../../core/types';

const PRESET_OPTIONS: Array<{ value: PresetName; label: string; description: string }> = [
  { value: 'default',    label: 'Default',    description: 'Balanced layout for everyday use' },
  { value: 'power_user', label: 'Power User', description: 'Maximum information density' },
  { value: 'minimal',    label: 'Minimal',    description: 'One widget per tab, distraction-free' },
  { value: 'focused',    label: 'Focused',    description: 'Tasks + capture + calendar — GTD style' },
];

const THEME_OPTIONS = [
  { value: 'light',  label: 'Light' },
  { value: 'dark',   label: 'Dark' },
  { value: 'system', label: 'System default' },
];

const TAB_OPTIONS = APP_TABS.map((t) => ({ value: t.id, label: t.label }));

export function SettingsTab() {
  const { settings, saveSettings, applyPreset } = useSettings();
  const [applyingPreset, setApplyingPreset] = useState<PresetName | null>(null);

  const handleApplyPreset = useCallback(
    async (preset: PresetName) => {
      if (!window.confirm(`Apply the "${preset}" preset? This will reset all tab layouts.`)) return;
      setApplyingPreset(preset);
      try {
        await applyPreset(preset);
      } finally {
        setApplyingPreset(null);
      }
    },
    [applyPreset]
  );

  const handleFactoryReset = useCallback(() => {
    if (
      !window.confirm(
        'Factory reset will clear ALL your layouts, settings, and snapshots. This cannot be undone. Continue?'
      )
    )
      return;
    applyPreset('default');
    saveSettings({
      theme: 'system',
      defaultTab: 'home',
      activePreset: 'default',
    });
  }, [applyPreset, saveSettings]);

  return (
    <div className="tab-root tab-root--settings settings-tab">
      <div className="settings-tab__content">

        {/* Presets */}
        <section className="settings-section">
          <Heading type={Heading.types.h2} value="Layout Presets" />
          <p className="settings-section__desc">
            Presets reset all tab layouts to a curated starting point. Your data is not affected.
          </p>
          <div className="preset-grid">
            {PRESET_OPTIONS.map((p) => (
              <div
                key={p.value}
                className={`preset-card ${settings.activePreset === p.value ? 'preset-card--active' : ''}`}
              >
                <p className="preset-card__name">{p.label}</p>
                <p className="preset-card__desc">{p.description}</p>
                <Button
                  kind={settings.activePreset === p.value ? Button.kinds.SECONDARY : Button.kinds.TERTIARY}
                  size={Button.sizes.SMALL}
                  onClick={() => handleApplyPreset(p.value)}
                  loading={applyingPreset === p.value}
                  disabled={applyingPreset !== null}
                >
                  {settings.activePreset === p.value ? 'Active' : 'Apply'}
                </Button>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* Appearance */}
        <section className="settings-section">
          <Heading type={Heading.types.h2} value="Appearance" />
          <div className="settings-field">
            <Label text="Theme" />
            <Dropdown
              options={THEME_OPTIONS}
              value={THEME_OPTIONS.find((o) => o.value === settings.theme) ?? THEME_OPTIONS[2]}
              onChange={(opt: { value: string } | null) =>
                saveSettings({ theme: (opt?.value ?? 'system') as 'light' | 'dark' | 'system' })
              }
              size={Dropdown.size.SMALL}
              className="settings-dropdown"
            />
          </div>
          <div className="settings-field">
            <Label text="Default tab on open" />
            <Dropdown
              options={TAB_OPTIONS}
              value={TAB_OPTIONS.find((o) => o.value === settings.defaultTab) ?? TAB_OPTIONS[0]}
              onChange={(opt: { value: string } | null) =>
                saveSettings({ defaultTab: opt?.value ?? 'home' })
              }
              size={Dropdown.size.SMALL}
              className="settings-dropdown"
            />
          </div>
        </section>

        <Divider />

        {/* Notifications */}
        <section className="settings-section">
          <Heading type={Heading.types.h2} value="Notifications" />
          <div className="settings-field settings-field--inline">
            <Label text="Show overdue badge on My Work tab" />
            <Toggle
              isSelected={settings.notificationPrefs?.showOverdueBadge ?? true}
              onChange={(val: boolean) =>
                saveSettings({
                  notificationPrefs: {
                    ...settings.notificationPrefs,
                    showOverdueBadge: val,
                  },
                })
              }
            />
          </div>
          <div className="settings-field settings-field--inline">
            <Label text="Show unread badge on Inbox tab" />
            <Toggle
              isSelected={settings.notificationPrefs?.showInboxBadge ?? true}
              onChange={(val: boolean) =>
                saveSettings({
                  notificationPrefs: {
                    ...settings.notificationPrefs,
                    showInboxBadge: val,
                  },
                })
              }
            />
          </div>
        </section>

        <Divider />

        {/* Danger Zone */}
        <section className="settings-section settings-section--danger">
          <Heading type={Heading.types.h2} value="Danger Zone" />
          <p className="settings-section__desc">
            Factory reset clears all layouts, snapshots, and custom settings. Your monday boards and data are NOT affected.
          </p>
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.MEDIUM}
            onClick={handleFactoryReset}
            className="btn-danger"
          >
            Factory Reset
          </Button>
        </section>

      </div>
    </div>
  );
}
